import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { BACKEND_URL } from "../../constants";
import {useAuth} from "../../navigation/AuthProvider";

// ------------------------------
// CSS-nyckelbilder (keyframes) för animationer
// ------------------------------
const animationStyles = `
  @keyframes fadeSlideIn {
    from {
      opacity: 0;
      transform: translateY(12px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes pulseDot {
    0%, 60%, 100% {
      transform: scale(1);
      opacity: 0.6;
    }
    30% {
      transform: scale(1.3);
      opacity: 1;
    }
  }

  .message-animate {
    animation: fadeSlideIn 0.25s ease-out forwards;
  }

  .loading-dots {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin-left: 8px;
  }

  .loading-dots span {
    width: 8px;
    height: 8px;
    background-color: #6b7280;
    border-radius: 50%;
    display: inline-block;
    animation: pulseDot 1.2s infinite ease-in-out;
  }

  .loading-dots span:nth-child(2) {
    animation-delay: 0.2s;
  }

  .loading-dots span:nth-child(3) {
    animation-delay: 0.4s;
  }
`;

// ------------------------------
// Stilar (oförändrade från tidigare)
// ------------------------------
const bubbleStyle = {
  position: "fixed",
  bottom: "24px",
  right: "24px",
  width: "64px",
  height: "64px",
  borderRadius: "50%",
  border: "none",
  cursor: "pointer",
  background: "linear-gradient(135deg,#2563eb,#3b82f6)",
  color: "#fff",
  fontSize: "28px",
  boxShadow: "0 10px 30px rgba(37,99,235,.35)",
  zIndex: 9998,
};

const getChatStyle = (isExpanded) => {
  if (isExpanded) {
    return {
      position: "fixed",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",

      width: "min(1100px, 60vw)",
      height: "min(900px, 92vh)",

      background: "#fff",
      borderRadius: "16px",
      overflow: "hidden",

      display: "flex",
      flexDirection: "column",

      boxShadow: "0 20px 60px rgba(0,0,0,.25)",
      zIndex: 9999,
    };
  }

  return {
    position: "fixed",

    bottom: "24px",
    right: "24px",

    width: "min(450px, calc(100vw - 32px))",
    height: "min(600px, calc(100vh - 100px))",

    background: "#fff",
    borderRadius: "16px",
    overflow: "hidden",

    display: "flex",
    flexDirection: "column",

    boxShadow: "0 20px 60px rgba(0,0,0,.15)",
    zIndex: 9998,
  };
};

const headerStyle = {
  padding: "12px 16px",
  background: "linear-gradient(135deg,#2563eb,#3b82f6)",
  color: "#fff",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
};

const leftHeaderStyle = {
  display: "flex",
  alignItems: "center",
  gap: "12px",
};

const expandButtonStyle = {
  border: "none",
  background: "rgba(255,255,255,0.2)",
  borderRadius: "8px",
  color: "#fff",
  fontSize: "18px",
  cursor: "pointer",
  padding: "6px 10px",
  lineHeight: 1,
};

const closeButtonStyle = {
  border: "none",
  background: "rgba(255,255,255,0.2)",
  borderRadius: "8px",
  color: "#fff",
  fontSize: "18px",
  cursor: "pointer",
  padding: "6px 10px",
  lineHeight: 1,
};

const messagesStyle = {
  flex: 1,
  overflowY: "auto",
  padding: "16px",
  background: "#f8fafc",
};

const inputContainerStyle = {
  borderTop: "1px solid #e5e7eb",
  padding: "12px",
  display: "flex",
  gap: "8px",
};

const inputStyle = {
  flex: 1,
  padding: "12px",
  borderRadius: "10px",
  border: "1px solid #d1d5db",
  outline: "none",
};

const sendButtonStyle = {
  border: "none",
  borderRadius: "10px",
  background: "#2563eb",
  color: "#fff",
  padding: "0 16px",
  cursor: "pointer",
};

const suggestionContainerStyle = {
  display: "flex",
  flexWrap: "wrap",
  gap: "8px",
  padding: "12px",
  borderBottom: "1px solid #e5e7eb",
};

const suggestionStyle = {
  border: "1px solid #dbeafe",
  background: "#eff6ff",
  color: "#2563eb",
  borderRadius: "999px",
  padding: "6px 12px",
  cursor: "pointer",
  fontSize: "12px",
};

const userMessageStyle = {
  background: "#2563eb",
  color: "#fff",
  padding: "10px 12px",
  borderRadius: "12px",
  marginBottom: "10px",
  maxWidth: "80%",
  marginLeft: "auto",
  marginTop: "10px",
};

const assistantMessageStyle = {
  background: "#fff",
  color: "#111827",
  padding: "10px 12px",
  borderRadius: "12px",
  marginBottom: "10px",
  maxWidth: "80%",
  border: "1px solid #e5e7eb",
};

const inlineSuggestionStyle = {
  ...suggestionStyle,
  marginTop: "6px",
  marginRight: "6px",
};

const loadingMessageStyle = {
  ...assistantMessageStyle,
  display: "flex",
  alignItems: "center",
  gap: "6px",
};

const quickSuggestions = [
  "Hur publicerar jag schema?",
  "Hur fungerar OB?",
  "Hur skapar jag ett projekt?",
  "Hur exporterar jag till Fortnox?",
];

export default function AiSupportChat() {

  const { user } = useAuth();

  console.log("AiSupportChat user:", user);
  const [open, setOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [question, setQuestion] = useState("");
  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content: "Hej! Jag är TimeSmart-assistenten. Vad behöver du hjälp med?",
    },
  ]);

  const messagesRef = useRef(null);
  const loadingTimeoutRef = useRef(null);

  useEffect(() => {
    if (messagesRef.current) {
      messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
    }
  }, [messages]);

  const askQuestion = async (customQuestion) => {
    const text = (customQuestion || question).trim();
    if (!text || loading) return; // Förhindra ny fråga medan laddning pågår

    // Lägg till användarens meddelande
    setMessages((prev) => [...prev, { role: "user", content: text }]);
    setQuestion("");
    setLoading(true);

    // Spara starttiden för att garantera minst 500 ms laddning
    const startTime = Date.now();
    try {
      const { data } = await axios.post(`${BACKEND_URL}/support/question`, {
        question: text,
        userId: user?._id,
      });

      // Vänta tills minst 500 ms har gått
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, 500 - elapsed);

      if (remaining > 0) {
        await new Promise((resolve) => {
          loadingTimeoutRef.current = setTimeout(resolve, remaining);
        });
      }

      if (data.status === "failure") {
        setMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            content: data.errorText || "Ett fel uppstod. Försök igen.",
          },
        ]);
      } else if (data.type === "article") {
        const article = data.article;
        let answerText = "";
        if (Array.isArray(article.content)) {
          answerText = article.content
            .map((step, idx) => `${idx + 1}. ${step}`)
            .join("\n\n");
        } else {
          answerText = article.content;
        }
        setMessages((prev) => [
          ...prev,
          { role: "assistant", content: answerText },
        ]);
      } else if (data.type === "suggestions") {
        setMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            content: "Jag är inte helt säker på vilken fråga du menar. Välj gärna någon av dessa:",
            suggestions: data.suggestions || [],
          },
        ]);
      } else if (data.type === "general") {
        setMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            content: data.answer || "Jag kunde inte generera ett svar just nu.",
          },
        ]);
      } else {
        setMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            content: "Jag kunde inte förstå svaret från servern. Försök igen.",
          },
        ]);
      }
    } catch (error) {
      console.error("Support chat error:", error);
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, 500 - elapsed);
      if (remaining > 0) {
        await new Promise((resolve) => {
          loadingTimeoutRef.current = setTimeout(resolve, remaining);
        });
      }
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: "Ett tekniskt fel uppstod. Prova igen om en stund.",
        },
      ]);
    } finally {
      setLoading(false);
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
        loadingTimeoutRef.current = null;
      }
    }
  };

  const toggleExpand = () => setIsExpanded((prev) => !prev);

  return (
    <>
      <style>{animationStyles}</style>
      {!open && (
        <button style={bubbleStyle} onClick={() => setOpen(true)}>
          💬
        </button>
      )}

      {open && (
        <div style={getChatStyle(isExpanded)}>
          <div style={headerStyle}>
            <div style={leftHeaderStyle}>
              <button onClick={toggleExpand} style={expandButtonStyle} title={isExpanded ? "Minimera" : "Förstora"}>
                {isExpanded ? "🗗" : "⛶"}
              </button>
              <div>
                <div style={{ fontWeight: 700 }}>TimeSmart Assistent</div>
                <div style={{ fontSize: "12px", opacity: 0.8 }}>Online</div>
              </div>
            </div>
            <button
              onClick={() => {
                setOpen(false);
                setIsExpanded(false);
              }}
              style={closeButtonStyle}
            >
              ✕
            </button>
          </div>

          <div style={suggestionContainerStyle}>
            {quickSuggestions.map((suggestion) => (
              <button key={suggestion} style={suggestionStyle} onClick={() => askQuestion(suggestion)}>
                {suggestion}
              </button>
            ))}
          </div>

          <div ref={messagesRef} style={messagesStyle}>
            {messages.map((message, idx) => (
              <div key={idx} className="message-animate">
                <div
                  style={{
                    ...(message.role === "user" ? userMessageStyle : assistantMessageStyle),
                    whiteSpace: "pre-line",
                  }}
                >
                  {message.content}
                </div>
                {message.suggestions && (
                  <div style={{ marginTop: "4px", marginBottom: "12px" }}>
                    {message.suggestions.map((sugg) => (
                      <button
                        key={sugg.title}
                        style={inlineSuggestionStyle}
                        onClick={() => askQuestion(sugg.title)}
                      >
                        {sugg.title}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
            {loading && (
              <div className="message-animate" style={loadingMessageStyle}>
                <span>Vänligen vänta</span>
                <div className="loading-dots">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            )}
          </div>

          <div style={inputContainerStyle}>
            <input
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder="Skriv din fråga..."
              style={inputStyle}
              onKeyDown={(e) => e.key === "Enter" && askQuestion()}
              disabled={loading}
            />
            <button style={sendButtonStyle} onClick={() => askQuestion()} disabled={loading}>
              ➤
            </button>
          </div>
        </div>
      )}
    </>
  );
}